import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

const defaultThemes = {
  dark: {
    name: 'Midnight Vibe',
    background: '#050505',
    surface: '#121212',
    surfaceHighlight: '#1E1E1E',
    primary: '#8B5CF6',
    secondary: '#22C55E',
    textMain: '#FFFFFF',
    textMuted: '#A1A1AA',
    border: '#27272a'
  },
  light: {
    name: 'Studio Day',
    background: '#F4F4F5',
    surface: '#FFFFFF',
    surfaceHighlight: '#E4E4E7',
    primary: '#7C3AED',
    secondary: '#16A34A',
    textMain: '#09090B',
    textMuted: '#71717A',
    border: '#E4E4E7'
  },
  vibrant: {
    name: 'Neon Hype',
    background: '#2E1065',
    surface: '#4C1D95',
    surfaceHighlight: '#5B21B6',
    primary: '#F472B6',
    secondary: '#22D3EE',
    textMain: '#FFFFFF',
    textMuted: '#E9D5FF',
    border: '#6D28D9'
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('dark');
  const [customColors, setCustomColors] = useState(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('radio-theme') || 'dark';
    const savedColors = localStorage.getItem('radio-custom-colors');
    setTheme(savedTheme);
    if (savedColors) {
      setCustomColors(JSON.parse(savedColors));
    }
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const changeTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('radio-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    
    // Apply theme colors as CSS variables
    const themeColors = defaultThemes[newTheme];
    if (themeColors) {
      applyColors(themeColors);
    }
  };

  const applyColors = (colors) => {
    const root = document.documentElement;
    root.style.setProperty('--background', colors.background);
    root.style.setProperty('--surface', colors.surface);
    root.style.setProperty('--surface-highlight', colors.surfaceHighlight);
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--secondary', colors.secondary);
    root.style.setProperty('--text-main', colors.textMain);
    root.style.setProperty('--text-muted', colors.textMuted);
    root.style.setProperty('--border-color', colors.border);
  };

  const setCustomColor = (colorKey, value) => {
    const newCustomColors = { ...customColors, [colorKey]: value };
    setCustomColors(newCustomColors);
    localStorage.setItem('radio-custom-colors', JSON.stringify(newCustomColors));
    document.documentElement.style.setProperty(`--${colorKey}`, value);
  };

  const resetColors = () => {
    setCustomColors(null);
    localStorage.removeItem('radio-custom-colors');
    applyColors(defaultThemes[theme]);
  };

  const currentTheme = defaultThemes[theme];

  return (
    <ThemeContext.Provider value={{
      theme,
      changeTheme,
      customColors,
      setCustomColor,
      resetColors,
      currentTheme,
      themes: defaultThemes
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
